// Pure generator logic for the CSS visual generator cluster. The React
// component owns state + live preview; these builders turn state into the
// copyable CSS/HTML snippets so the logic stays testable and framework-free.

export type CssVisualVariant =
  | 'css-clip-path-generator'
  | 'css-cubic-bezier-generator'
  | 'css-background-pattern-generator'
  | 'css-checkbox-generator'
  | 'css-switch-generator';

// --- clip-path ------------------------------------------------------------

export type ClipPathShape =
  | 'triangle'
  | 'circle'
  | 'ellipse'
  | 'hexagon'
  | 'pentagon'
  | 'message'
  | 'arrow-right'
  | 'close'
  | 'frame'
  | 'custom';

export const CLIP_PATH_PRESETS: Record<Exclude<ClipPathShape, 'custom'>, string> = {
  triangle: 'polygon(50% 0%, 0% 100%, 100% 100%)',
  circle: 'circle(50% at 50% 50%)',
  ellipse: 'ellipse(40% 50% at 50% 50%)',
  hexagon: 'polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)',
  pentagon: 'polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)',
  message: 'polygon(0% 0%, 100% 0%, 100% 75%, 75% 75%, 75% 100%, 50% 75%, 0% 75%)',
  'arrow-right': 'polygon(0% 20%, 60% 20%, 60% 0%, 100% 50%, 60% 100%, 60% 80%, 0% 80%)',
  close:
    'polygon(20% 0%, 0% 20%, 30% 50%, 0% 80%, 20% 100%, 50% 70%, 80% 100%, 100% 80%, 70% 50%, 100% 20%, 80% 0%, 50% 30%)',
  frame:
    'polygon(0% 0%, 0% 100%, 25% 100%, 25% 25%, 75% 25%, 75% 75%, 25% 75%, 25% 100%, 100% 100%, 100% 0%)',
};

export const CLIP_PATH_LABELS: Record<ClipPathShape, string> = {
  triangle: 'Triangle',
  circle: 'Circle',
  ellipse: 'Ellipse',
  hexagon: 'Hexagon',
  pentagon: 'Pentagon',
  message: 'Message',
  'arrow-right': 'Arrow right',
  close: 'Close (X)',
  frame: 'Frame',
  custom: 'Custom polygon',
};

export function clipPathValue(shape: ClipPathShape, customPoints: string): string {
  if (shape === 'custom') {
    const points = customPoints.trim() || '50% 0%, 0% 100%, 100% 100%';
    return `polygon(${points})`;
  }
  return CLIP_PATH_PRESETS[shape];
}

export function clipPathCss(shape: ClipPathShape, customPoints: string): string {
  const value = clipPathValue(shape, customPoints);
  return `-webkit-clip-path: ${value};\nclip-path: ${value};`;
}

// --- cubic-bezier ---------------------------------------------------------

export type BezierState = {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
};

export const BEZIER_PRESETS: Record<string, BezierState> = {
  ease: { x1: 0.25, y1: 0.1, x2: 0.25, y2: 1 },
  'ease-in': { x1: 0.42, y1: 0, x2: 1, y2: 1 },
  'ease-out': { x1: 0, y1: 0, x2: 0.58, y2: 1 },
  'ease-in-out': { x1: 0.42, y1: 0, x2: 0.58, y2: 1 },
  linear: { x1: 0, y1: 0, x2: 1, y2: 1 },
};

function trim(value: number): string {
  return String(Number(value.toFixed(3)));
}

export function bezierValue(state: BezierState): string {
  return `cubic-bezier(${trim(state.x1)}, ${trim(state.y1)}, ${trim(state.x2)}, ${trim(state.y2)})`;
}

export function bezierCss(state: BezierState, duration: number): string {
  const value = bezierValue(state);
  return `transition-timing-function: ${value};\ntransition: all ${duration}s ${value};`;
}

// SVG path for the easing curve inside a unit box of the given pixel size.
export function bezierCurvePath(state: BezierState, size: number): string {
  // Flip Y so the curve reads bottom-left to top-right like DevTools.
  const px = (v: number) => v * size;
  const py = (v: number) => size - v * size;
  return `M0,${py(0)} C${px(state.x1)},${py(state.y1)} ${px(state.x2)},${py(state.y2)} ${px(1)},${py(1)}`;
}

// --- background pattern ---------------------------------------------------

export type PatternKind = 'stripes' | 'checkerboard' | 'dots' | 'diagonal' | 'grid';

export type PatternState = {
  kind: PatternKind;
  color1: string;
  color2: string;
  size: number;
};

export const PATTERN_LABELS: Record<PatternKind, string> = {
  stripes: 'Stripes',
  checkerboard: 'Checkerboard',
  dots: 'Dots',
  diagonal: 'Diagonal stripes',
  grid: 'Grid',
};

// Returns the value that goes into `background-color` + `background-image` +
// `background-size`, assembled into a single declaration block.
export function patternCss(state: PatternState): string {
  const { kind, color1: fg, color2: bg, size } = state;
  if (kind === 'stripes') {
    return [
      `background-color: ${bg};`,
      `background-image: linear-gradient(90deg, ${fg} 50%, transparent 50%);`,
      `background-size: ${size}px ${size}px;`,
    ].join('\n');
  }
  if (kind === 'diagonal') {
    return [
      `background-color: ${bg};`,
      `background-image: repeating-linear-gradient(45deg, ${fg} 0, ${fg} ${Math.round(
        size / 2,
      )}px, transparent ${Math.round(size / 2)}px, transparent ${size}px);`,
    ].join('\n');
  }
  if (kind === 'checkerboard') {
    const half = Math.round(size / 2);
    return [
      `background-color: ${bg};`,
      `background-image: linear-gradient(45deg, ${fg} 25%, transparent 25%, transparent 75%, ${fg} 75%), linear-gradient(45deg, ${fg} 25%, transparent 25%, transparent 75%, ${fg} 75%);`,
      `background-size: ${size}px ${size}px;`,
      `background-position: 0 0, ${half}px ${half}px;`,
    ].join('\n');
  }
  if (kind === 'dots') {
    return [
      `background-color: ${bg};`,
      `background-image: radial-gradient(${fg} 20%, transparent 21%);`,
      `background-size: ${size}px ${size}px;`,
    ].join('\n');
  }
  // grid
  return [
    `background-color: ${bg};`,
    `background-image: linear-gradient(${fg} 1px, transparent 1px), linear-gradient(90deg, ${fg} 1px, transparent 1px);`,
    `background-size: ${size}px ${size}px;`,
  ].join('\n');
}

// The same pattern expressed as React inline-style properties for the preview.
export function patternPreviewStyle(state: PatternState): {
  backgroundColor: string;
  backgroundImage: string;
  backgroundSize?: string;
  backgroundPosition?: string;
} {
  const { kind, color1: fg, color2: bg, size } = state;
  if (kind === 'stripes') {
    return {
      backgroundColor: bg,
      backgroundImage: `linear-gradient(90deg, ${fg} 50%, transparent 50%)`,
      backgroundSize: `${size}px ${size}px`,
    };
  }
  if (kind === 'diagonal') {
    const half = Math.round(size / 2);
    return {
      backgroundColor: bg,
      backgroundImage: `repeating-linear-gradient(45deg, ${fg} 0, ${fg} ${half}px, transparent ${half}px, transparent ${size}px)`,
    };
  }
  if (kind === 'checkerboard') {
    const half = Math.round(size / 2);
    return {
      backgroundColor: bg,
      backgroundImage: `linear-gradient(45deg, ${fg} 25%, transparent 25%, transparent 75%, ${fg} 75%), linear-gradient(45deg, ${fg} 25%, transparent 25%, transparent 75%, ${fg} 75%)`,
      backgroundSize: `${size}px ${size}px`,
      backgroundPosition: `0 0, ${half}px ${half}px`,
    };
  }
  if (kind === 'dots') {
    return {
      backgroundColor: bg,
      backgroundImage: `radial-gradient(${fg} 20%, transparent 21%)`,
      backgroundSize: `${size}px ${size}px`,
    };
  }
  return {
    backgroundColor: bg,
    backgroundImage: `linear-gradient(${fg} 1px, transparent 1px), linear-gradient(90deg, ${fg} 1px, transparent 1px)`,
    backgroundSize: `${size}px ${size}px`,
  };
}

// --- checkbox -------------------------------------------------------------

export type CheckboxState = {
  size: number;
  radius: number;
  color: string;
  borderColor: string;
};

export function checkboxSnippet(state: CheckboxState): string {
  const { size, radius, color, borderColor } = state;
  return `<label class="c-checkbox">
  <input type="checkbox" checked />
  <span class="c-checkbox__box"></span>
  <span>Label</span>
</label>

<style>
.c-checkbox {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
}
.c-checkbox input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.c-checkbox__box {
  position: relative;
  width: ${size}px;
  height: ${size}px;
  border: 2px solid ${borderColor};
  border-radius: ${radius}px;
  transition: background 0.15s ease, border-color 0.15s ease;
}
.c-checkbox__box::after {
  content: "";
  position: absolute;
  left: 35%;
  top: 15%;
  width: ${Math.max(3, Math.round(size * 0.22))}px;
  height: ${Math.max(6, Math.round(size * 0.5))}px;
  border: solid #ffffff;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg) scale(0);
  transition: transform 0.15s ease;
}
.c-checkbox input:checked + .c-checkbox__box {
  background: ${color};
  border-color: ${color};
}
.c-checkbox input:checked + .c-checkbox__box::after {
  transform: rotate(45deg) scale(1);
}
.c-checkbox input:focus-visible + .c-checkbox__box {
  outline: 2px solid ${color};
  outline-offset: 2px;
}
</style>`;
}

// --- switch ---------------------------------------------------------------

export type SwitchState = {
  width: number;
  onColor: string;
  offColor: string;
};

export function switchDimensions(width: number): {
  width: number;
  height: number;
  knob: number;
  travel: number;
} {
  const height = Math.round(width * 0.56);
  const knob = height - 6;
  const travel = width - knob - 6;
  return { width, height, knob, travel };
}

export function switchSnippet(state: SwitchState): string {
  const { width, onColor, offColor } = state;
  const { height, knob, travel } = switchDimensions(width);
  return `<label class="c-switch">
  <input type="checkbox" checked />
  <span class="c-switch__track">
    <span class="c-switch__thumb"></span>
  </span>
</label>

<style>
.c-switch {
  display: inline-flex;
  cursor: pointer;
}
.c-switch input {
  position: absolute;
  opacity: 0;
  width: 0;
  height: 0;
}
.c-switch__track {
  position: relative;
  display: inline-block;
  width: ${width}px;
  height: ${height}px;
  background: ${offColor};
  border-radius: ${Math.round(height / 2)}px;
  transition: background 0.2s ease;
}
.c-switch__thumb {
  position: absolute;
  top: 3px;
  left: 3px;
  width: ${knob}px;
  height: ${knob}px;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease;
}
.c-switch input:checked + .c-switch__track {
  background: ${onColor};
}
.c-switch input:checked + .c-switch__track .c-switch__thumb {
  transform: translateX(${travel}px);
}
.c-switch input:focus-visible + .c-switch__track {
  outline: 2px solid ${onColor};
  outline-offset: 2px;
}
</style>`;
}
